import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Image,
  Modal,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { CommunityPostCard } from './CommunityPostCard';
import PostGridDisplay from './PostGridDisplay';
import { useTheme } from '../contexts/ThemeContext';
import { EventPostCard } from './events/EventPostCard';
import { EventPostFormModal } from './events/EventPostFormModal';
import { deleteEventPost } from '../utils/eventPostsService';

// Delete confirm matches the Events page dialog.
const CONFIRM_BG = '#231f20';
const CONFIRM_ACCENT = '#f5f1ea';
// Alert.alert buttons do nothing on web, so deletes confirm in a Modal (same pattern as EventsList).
const DELETE_COPY = {
  post: { title: 'Delete post?', message: 'Remove this community listing?', error: 'Could not delete post. Please try again.' },
  gear: { title: 'Delete listing?', message: 'Remove this gear swap listing?', error: 'Could not delete listing. Please try again.' },
  event: { title: 'Delete event?', message: 'This will permanently delete the event and its event chat.', error: 'Could not delete event. Please try again.' },
};
const LISTING_TYPE_COLORS = { SALE: '#0a59f0', TRADE: '#03bb8d', ISO: '#4e5067' };

function sortByCreatedDesc(items) {
  return [...items].sort((a, b) => {
    const at = a.createdAt?.toMillis?.() ?? (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
    const bt = b.createdAt?.toMillis?.() ?? (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
    return bt - at;
  });
}

export function ProfileMyPostsTab({ navigation, userData, colors, profileStyles }) {
  const { isDarkMode } = useTheme();
  const sectionTitleColor = isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement;
  const emptyTextColor    = isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)';
  const [communityPosts, setCommunityPosts] = useState([]);
  const [gearListings, setGearListings] = useState([]);
  const [eventPosts, setEventPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [communityOptionsId, setCommunityOptionsId] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);

  const loadPosts = useCallback(async () => {
    if (!userData?.uid) {
      setCommunityPosts([]);
      setGearListings([]);
      setEventPosts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const db = getFirestore();
      const uid = userData.uid;
      const [commSnap, gearSnap, eventsSnap] = await Promise.all([
        getDocs(query(collection(db, 'communityPosts'), where('userId', '==', uid))),
        getDocs(query(collection(db, 'gearSwapPosts'), where('userId', '==', uid))),
        getDocs(query(collection(db, 'eventPosts'), where('userId', '==', uid))),
      ]);
      const comm = commSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        entityType: 'post',
      }));
      const gear = gearSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const events = eventsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCommunityPosts(sortByCreatedDesc(comm));
      setGearListings(sortByCreatedDesc(gear));
      setEventPosts(sortByCreatedDesc(events));
    } catch (e) {
      console.error('ProfileMyPostsTab load error:', e);
      setCommunityPosts([]);
      setGearListings([]);
      setEventPosts([]);
    } finally {
      setLoading(false);
    }
  }, [userData?.uid]);

  useFocusEffect(
    useCallback(() => {
      loadPosts();
    }, [loadPosts])
  );

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteNotice, setDeleteNotice] = useState('');

  const requestDelete = (kind, item) => {
    setDeleteError('');
    setDeleteNotice('');
    setCommunityOptionsId(null);
    setDeleteTarget({ kind, item });
  };
  const handleDeleteCommunityPost = (post) => requestDelete('post', post);
  const handleDeleteGear = (listing) => requestDelete('gear', listing);
  const handleDeleteEvent = (event) => requestDelete('event', event);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const { kind, item } = deleteTarget;
    setDeleting(true);
    setDeleteError('');
    try {
      if (kind === 'event') {
        const cleanupErrors = await deleteEventPost(item);
        setEventPosts((prev) => prev.filter((p) => p.id !== item.id));
        if (cleanupErrors.length) {
          console.error('Event deleted, but some event media could not be removed:', cleanupErrors);
          setDeleteNotice('The event was deleted, but some attached media could not be removed.');
        }
      } else {
        const collectionName = kind === 'post' ? 'communityPosts' : 'gearSwapPosts';
        await deleteDoc(doc(getFirestore(), collectionName, item.id));
        if (kind === 'post') setCommunityPosts((prev) => prev.filter((p) => p.id !== item.id));
        else setGearListings((prev) => prev.filter((p) => p.id !== item.id));
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error(`Delete ${kind} failed`, err);
      setDeleteError(DELETE_COPY[kind].error);
    } finally {
      setDeleting(false);
    }
  };

  const renderGearCard = (item) => {
    const typeColor = LISTING_TYPE_COLORS[item.listingType] || '#666';
    const postItems = Array.isArray(item.items) ? item.items : [];
    const headerColor = item.headerTextColor || undefined;
    return (
      <View key={item.id} style={[styles.gearCard, { borderColor: typeColor }]}>
        {item.cardBackground?.type === 'color' && item.cardBackground.value ? (
          <View style={[styles.gearCardBg, { backgroundColor: item.cardBackground.value }]} />
        ) : null}
        {item.cardBackground?.type === 'image' && item.cardBackground.value ? (
          <Image source={{ uri: item.cardBackground.value }} style={styles.gearCardBg} resizeMode="cover" />
        ) : null}
        <View style={styles.gearHeader}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={[styles.gearTitle, headerColor ? { color: headerColor } : undefined]} numberOfLines={1}>
              {item.displayName || 'Listing'}
            </Text>
            {item.location ? (
              <Text style={[styles.gearSub, headerColor ? { color: headerColor } : undefined]} numberOfLines={1}>
                {item.location}
              </Text>
            ) : null}
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={[styles.gearBadge, { color: typeColor }]}>{item.listingType || '—'}</Text>
            {item.listingType === 'SALE' && item.price ? (
              <Text style={styles.gearPrice}>${item.price}</Text>
            ) : null}
          </View>
        </View>
        <PostGridDisplay items={postItems} cardBackground={null} suppressEmptyWidgetHints />
        <View style={styles.gearActions}>
          <TouchableOpacity
            style={styles.gearBtn}
            onPress={() =>
              navigation.navigate('GearSwapPostCreate', { editListingId: item.id, existingListing: item })
            }
          >
            <Text style={styles.gearBtnText}>Edit</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.gearBtnDanger} onPress={() => handleDeleteGear(item)}>
            <Text style={styles.gearBtnDangerText}>Delete</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.centered, { paddingVertical: 40 }]}>
        <ActivityIndicator size="large" color={colors.purpleReplacement || '#9b59b6'} />
        <Text style={[profileStyles.inputLabel, { color: '#bdc3c7', marginTop: 12, textAlign: 'center' }]}>
          Loading your posts…
        </Text>
      </View>
    );
  }

  return (
    <View style={{ width: '100%', maxWidth: 640, alignSelf: 'center', paddingBottom: 24 }}>
      <Text style={[profileStyles.cardTitle, { color: sectionTitleColor, marginTop: 8, marginBottom: 8 }]}>
        My Posts
      </Text>
      <Text style={[styles.sectionTitle, { color: sectionTitleColor }]}>Community directory</Text>
      {communityPosts.length === 0 ? (
        <Text style={[styles.emptyText, { color: emptyTextColor }]}>No community listings yet.</Text>
      ) : (
        communityPosts.map((post) => (
          <View key={post.id} style={styles.cardWrap}>
            <CommunityPostCard
              post={post}
              onPress={() => {}}
              onFolderPress={() => {}}
              showFolderButton={false}
              showMessageButton={false}
              rightActions={
                <TouchableOpacity
                  style={styles.optionsBtn}
                  onPress={(e) => {
                    if (e?.stopPropagation) e.stopPropagation();
                    setCommunityOptionsId((prev) => (prev === post.id ? null : post.id));
                  }}
                >
                  <Ionicons name="ellipsis-vertical" size={22} color="#555" />
                </TouchableOpacity>
              }
            />
            {communityOptionsId === post.id && (
              <View style={styles.optionsMenu}>
                <TouchableOpacity
                  style={styles.optionsItem}
                  onPress={() => {
                    setCommunityOptionsId(null);
                    navigation.navigate('CommunityPostCreate', {
                      editPostId: post.id,
                      existingPost: post,
                    });
                  }}
                >
                  <Text style={styles.optionsItemText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.optionsItem}
                  onPress={() => {
                    setCommunityOptionsId(null);
                    handleDeleteCommunityPost(post);
                  }}
                >
                  <Text style={[styles.optionsItemText, { color: '#e74c3c' }]}>Delete</Text>
                </TouchableOpacity>
              </View>
            )}
            {post.postAsOrganizationName ? (
              <Text style={styles.orgHint}>Posted as: {post.postAsOrganizationName}</Text>
            ) : null}
          </View>
        ))
      )}

      <Text style={[styles.sectionTitle, { color: sectionTitleColor, marginTop: 28 }]}>Gear swap</Text>
      {gearListings.length === 0 ? (
        <Text style={[styles.emptyText, { color: emptyTextColor }]}>No gear swap listings yet.</Text>
      ) : (
        gearListings.map(renderGearCard)
      )}

      <Text style={[styles.sectionTitle, { color: sectionTitleColor, marginTop: 28 }]}>Events</Text>
      {deleteNotice ? (
        <Text style={[styles.emptyText, { color: sectionTitleColor }]} accessibilityLiveRegion="polite">{deleteNotice}</Text>
      ) : null}
      {eventPosts.length === 0 ? (
        <Text style={[styles.emptyText, { color: emptyTextColor }]}>No event posts yet.</Text>
      ) : (
        // BUG-105: RN-web wrappers are z-index 0, so an open options menu would sit under the next
        // card. Earlier cards stack higher so Delete stays clickable.
        eventPosts.map((event, index) => (
          <View key={event.id} style={[styles.cardWrap, { zIndex: eventPosts.length - index }]}>
            <EventPostCard
              event={event}
              currentUser={userData}
              onEdit={(selectedEvent) => {
                setEditingEvent(selectedEvent);
                setShowEventForm(true);
              }}
              onDelete={handleDeleteEvent}
            />
          </View>
        ))
      )}
      <EventPostFormModal
        visible={showEventForm}
        onClose={() => {
          setShowEventForm(false);
          setEditingEvent(null);
        }}
        currentUser={userData}
        event={editingEvent}
        onPosted={() => {
          setShowEventForm(false);
          setEditingEvent(null);
          loadPosts();
        }}
      />
      <Modal
        visible={Boolean(deleteTarget)}
        transparent
        animationType="fade"
        onRequestClose={() => { if (!deleting) setDeleteTarget(null); }}
      >
        <View style={styles.confirmBackdrop}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>{deleteTarget ? DELETE_COPY[deleteTarget.kind].title : ''}</Text>
            <Text style={styles.confirmMessage}>
              {deleteTarget?.kind === 'event'
                ? `This will permanently delete “${deleteTarget.item.title || 'this event'}” and its event chat.`
                : deleteTarget ? DELETE_COPY[deleteTarget.kind].message : ''}
            </Text>
            {deleteError ? <Text style={styles.confirmError}>{deleteError}</Text> : null}
            <View style={styles.confirmActions}>
              <TouchableOpacity
                onPress={() => setDeleteTarget(null)}
                disabled={deleting}
                style={[styles.confirmButton, styles.cancelDeleteButton]}
                accessibilityRole="button"
              >
                <Text style={styles.cancelDeleteText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={confirmDelete}
                disabled={deleting}
                style={[styles.confirmButton, styles.confirmDeleteButton, deleting && { opacity: 0.6 }]}
                accessibilityRole="button"
              >
                {deleting
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.confirmDeleteText}>Delete</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  },
  emptyText: {
    color: 'rgba(255,255,255,0.55)',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    marginBottom: 8,
  },
  cardWrap: {
    marginBottom: 16,
    position: 'relative',
  },
  optionsBtn: {
    padding: 4,
  },
  optionsMenu: {
    position: 'absolute',
    right: 8,
    top: 44,
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingVertical: 4,
    zIndex: 20,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    minWidth: 120,
  },
  optionsItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  optionsItemText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#333',
  },
  orgHint: {
    marginTop: 6,
    fontSize: 12,
    color: 'rgba(255,255,255,0.6)',
    fontFamily: 'VastagoGrotesk',
  },
  gearCard: {
    borderRadius: 12,
    borderWidth: 3,
    overflow: 'visible',
    padding: 14,
    marginBottom: 16,
    backgroundColor: '#feffe4',
    position: 'relative',
  },
  gearCardBg: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 9,
    overflow: 'hidden',
    zIndex: 0,
  },
  gearHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 10,
    zIndex: 1,
  },
  gearTitle: {
    fontFamily: 'Computer Says No',
    fontSize: 22,
    color: '#000',
  },
  gearSub: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    color: '#333',
  },
  gearBadge: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 15,
    fontWeight: '700',
  },
  gearPrice: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '700',
    color: '#000',
  },
  gearActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
    zIndex: 1,
  },
  gearBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: '#3ba378',
  },
  gearBtnText: {
    color: '#fff',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    fontSize: 14,
  },
  gearBtnDanger: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#e74c3c',
  },
  gearBtnDangerText: {
    color: '#e74c3c',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    fontSize: 14,
  },
  confirmBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  confirmCard: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: CONFIRM_BG,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: CONFIRM_ACCENT,
    padding: 22,
    gap: 12,
  },
  confirmTitle: {
    color: CONFIRM_ACCENT,
    fontSize: 20,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  confirmMessage: {
    color: CONFIRM_ACCENT,
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'VastagoGrotesk',
  },
  confirmError: {
    color: '#ff7b7b',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  confirmActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 6,
  },
  confirmButton: {
    minWidth: 90,
    minHeight: 42,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    paddingHorizontal: 16,
  },
  cancelDeleteButton: {
    borderWidth: 1,
    borderColor: CONFIRM_ACCENT,
  },
  cancelDeleteText: {
    color: CONFIRM_ACCENT,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  confirmDeleteButton: {
    backgroundColor: '#b62b35',
  },
  confirmDeleteText: {
    color: '#fff',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
});
